import { useEffect, useState } from 'react';

import {
  listLeaderboardEntries,
  listLeaderboardProfiles,
  type LeaderboardProfile,
  type LeaderboardRecord,
} from '@lib/leaderboard';

export function useLeaderboardData(limit: number) {
  const [entries, setEntries] = useState<LeaderboardRecord[]>([]);
  const [profiles, setProfiles] = useState<Record<string, LeaderboardProfile>>(
    {}
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const fetchLimit = Number.isFinite(limit) && limit > 0 ? limit : 25;

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const leaderboardEntries = await listLeaderboardEntries(fetchLimit);
        if (!active) return;
        setEntries(leaderboardEntries);

        const ids = Array.from(
          new Set(
            leaderboardEntries
              .map((entry) => entry.user_id)
              .filter(Boolean) as string[]
          )
        );

        if (ids.length > 0) {
          try {
            const leaderboardProfiles = await listLeaderboardProfiles(ids);
            if (!active) return;
            const mapped: Record<string, LeaderboardProfile> = {};

            leaderboardProfiles.forEach((profile) => {
              if (profile.user_id) {
                mapped[profile.user_id] = profile;
              }
            });

            setProfiles(mapped);
          } catch (innerError) {
            if (active) {
              console.error('Failed to load leaderboard profiles', innerError);
            }
            setProfiles({});
          }
        } else {
          setProfiles({});
        }

        setError(null);
      } catch (loadError) {
        if (!active) return;
        console.error('Failed to load leaderboard', loadError);
        setEntries([]);
        setProfiles({});
        setError('Unable to load leaderboard data right now.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [limit]);

  return {
    entries,
    profiles,
    loading,
    error,
  };
}
